import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAll } from 'src/shared/services/groupMessageSmsService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { Box } from '@mui/material';
import { GroupMessageSmsSearchFilter } from '../models/groupMessageSmsSearchFilter';
import { GroupMessageSmsResponse } from '../models/groupMessageSmsRespons';

type GroupMessageSmsGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: GroupMessageSmsSearchFilter;
  searchFilterCallback: (filter: GroupMessageSmsSearchFilter) => void;
};

export const GroupMessageSmsGrid = (props: GroupMessageSmsGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['groupMessage']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const [rows, setRows] = useState<GroupMessageSmsResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAll(searchFilter);
      if (isMounted()) {
        const memberResult = result.data.results;
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(memberResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.GroupMessageSmsEdit}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('groupMessage:tableHeaders.name'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.name || t('groupMessage:tableHeaders.noSubject')}</>;
      },
    },
    {
      field: 'sendDate',
      headerName: t('groupMessage:tableHeaders.sendDate'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { sendDate } = params.row;
        return <>{sendDate || t('groupMessage:tableHeaders.notSent')}</>;
      },
    },
    {
      field: 'sender',
      headerName: t('groupMessage:tableHeaders.sender'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.sender}</>;
      },
    },
    {
      field: 'id',
      headerName: t('groupMessage:tableHeaders.id'),
      sortable: false,
      flex: 0.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.id}</>;
      },
    },
  ];

  return (
    <Box ref={scrollRef}>
      <DataGrid<GroupMessageSmsResponse>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{
          id: false,
        }}
        dataModel={data}
        checkboxSelection={false}
        onRowClick={onRowClick}
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
